import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Plan } from 'constants/plan.constants';
import _ from 'lodash';
import {
	getCountriesAsync,
	getCountriesIncludeForbiddenAsync,
	getCurrencyAsync,
	getBankListAsync,
	getPlansAsync,
	getPaymentPurpose,
} from 'services/global.services';

interface globalState {
	startLoadingCurrencies: boolean;
	startLoadingBanks: boolean;
	startLoadingPlans: boolean;
	loadingApprovals: boolean;
	loadingCurrencies: boolean;
	loadingCountries: boolean;
	loadingBanks: boolean;
	loadingPlans: boolean;
	loadingPaymentPurpose: boolean;
	currencies: globalRecord[];
	countries: globalRecord[];
	banks: globalRecord[];
	currencyPairs: pairs;
	plans: Plan[];
	paymentPurpose: Object[];
	countriesIncludeForbidden: globalRecord[];
	version: string;
}

type globalRecord = {
	id: number;
	name: string;
};
type pairs = { [key: string]: string[] };

const initialState: globalState = {
	startLoadingBanks: false,
	startLoadingCurrencies: false,
	startLoadingPlans: false,
	loadingApprovals: true,
	loadingCurrencies: false,
	loadingCountries: false,
	loadingBanks: false,
	loadingPlans: false,
	loadingPaymentPurpose: false,
	currencies: [],
	countries: [],
	banks: [],
	currencyPairs: {},
	plans: [],
	countriesIncludeForbidden: [],
	paymentPurpose: [],
	version: 'n/a',
};

export const globalSlice = createSlice({
	name: 'global',
	initialState,
	reducers: {
		setStartLoadingCurrencies: (state) => {
			state.startLoadingCurrencies = true;
		},
		setLoadingApprovals: (state, action: PayloadAction<boolean>) => {
			state.loadingApprovals = action.payload;
		},
		setCurrencyPairs: (state, action: PayloadAction<pairs>) => {
			state.currencyPairs = action.payload;
		},
		setVersion: (state, { payload }) => {
			state.version = payload;
		},
	},
	extraReducers: {
		[getCountriesAsync.fulfilled.type]: (state, { payload }) => {
			state.countries = payload.allowedCountries;
			state.loadingCountries = false;
		},
		[getCountriesAsync.pending.type]: (state) => {
			state.loadingCountries = true;
		},
		[getCountriesAsync.rejected.type]: (state) => {
			state.loadingCountries = false;
		},
		[getCurrencyAsync.fulfilled.type]: (state, { payload }) => {
			state.currencies = [];
			payload.allowedCurrencies.forEach((item) => {
				state.currencies.push({ ...item, customName: item.code + ' - ' + item.name });
			});
			//state.currencies = payload.allowedCurrencies;
			state.loadingCurrencies = false;
		},
		[getCurrencyAsync.pending.type]: (state) => {
			state.startLoadingCurrencies = true;
			state.loadingCurrencies = true;
		},
		[getCurrencyAsync.rejected.type]: (state) => {
			state.loadingCurrencies = false;
		},
		[getBankListAsync.fulfilled.type]: (state, { payload }) => {
			payload.banks.map((bank: any) => {
				bank.bankName = bank.bankName.trim();
			});
			state.banks = payload.banks;
			state.loadingBanks = false;
		},
		[getBankListAsync.pending.type]: (state) => {
			state.startLoadingBanks = true;
			state.loadingBanks = true;
		},
		[getBankListAsync.rejected.type]: (state) => {
			state.loadingBanks = false;
		},
		[getPlansAsync.fulfilled.type]: (state, { payload }) => {
			state.plans = payload;
			state.loadingPlans = false;
		},
		[getPlansAsync.pending.type]: (state) => {
			state.startLoadingPlans = true;
			state.loadingPlans = true;
		},
		[getPlansAsync.rejected.type]: (state) => {
			state.loadingPlans = false;
		},
		[getCountriesIncludeForbiddenAsync.fulfilled.type]: (state, { payload }) => {
			state.countriesIncludeForbidden = payload.allCountries;
		},
		[getPaymentPurpose.pending.type]: (state) => {
			state.loadingPaymentPurpose = true;
		},
		[getPaymentPurpose.fulfilled.type]: (state, { payload }) => {
			state.paymentPurpose = payload?.paymentPurposeCodes;
			state.loadingPaymentPurpose = false;
		},
		[getPaymentPurpose.rejected.type]: (state) => {
			state.loadingPaymentPurpose = false;
		},
	},
});
const globalSelector = (state) => state.global;
export const getCurrencyPairs = (state: any) => {
	return state.global.currencyPairs;
};

export const getCountries = (state: any) => {
	return state.global.countries;
};

export const getCurrencies = (state: any) => {
	return state.global.currencies;
};

export const getCountriesObj = createSelector([globalSelector], (state: any) => {
	return _.keyBy(state.countries, 'id');
});

export const getCurrenciesObj = createSelector([globalSelector], (state: any) => {
	return _.keyBy(state.currencies, 'id');
});

export const getCurrencyById = (id?: number) => (state: any) => {
	const obj = _.keyBy(state.global.currencies, 'id');
	return id && _.has(obj, id) ? _.get(obj, id) : undefined;
};

export const getCountryById = (id?: number) => (state: any) => {
	const obj = _.keyBy(state.global.countries, 'id');
	return id && _.has(obj, id) ? _.get(obj, id) : undefined;
};

export const getBankList = (state: any) => {
	return state.global.banks;
};

export const getBankListObj = (state: any) => {
	return _.keyBy(state.global.banks, 'bankCode');
};

export const getLoadingApprovals = (state: any) => {
	return state.global.loadingApprovals;
};
export const loadingCountries = (state: any) => {
	return state.global.loadingCountries;
};

export const loadingCurrencies = createSelector([globalSelector], (state: any) => {
	return {
		loadingCurrencies: state.loadingCurrencies,
		startLoadingCurrencies: state.startLoadingCurrencies,
	};
});

export const loadingBanks = (state: any) => {
	return {
		loadingBanks: state.global.loadingBanks,
		startLoadingBanks: state.global.startLoadingBanks,
	};
};

export const loadingPlans = (state: any) => {
	return state.global.loadingPlans;
};

export const getPlansObj = (state: any) => {
	return _.keyBy(state.global.plans, 'id');
};

export const getPlans = (state: any) => {
	return state.global.plans;
};

export const getCountriesIncludeForbidden = (state: any) => {
	return state.global.countriesIncludeForbidden;
};

export const getVersion = (state: any) => {
	return state.global.version;
};

export const { setCurrencyPairs, setVersion, setLoadingApprovals } = globalSlice.actions;

export default globalSlice.reducer;
